import './Dashboard.css'

import React, { useMemo, useState } from 'react'
import { ManagerRoles, Roles } from '@/services/repository/users'
import { companyManagerMenuSections, coordinatorMenuSections, Menu, MenuProps, MenuSection, operatorMenuSections, SectionMenuPopup, servicemanMenuSections, superadminMenuSections, TopMenu, userMenuSections } from './menus'
import { useAppContext } from '@/context/app'
import { LoggedInLayout } from '@/components/layout/LoggedInLayout'
import { LogoutButton } from '@/components/layout/NavBar'
import { UserLangsButton } from '@/components/layout/Langs'
import { useResponsiveChange } from '@/common/device'
import MapComponent from '@/components/Map/Map'
import { Spinner } from '@/components/spinner'
import { useModal } from '@/components/modal/Modal'
import { useNavigate } from 'react-router-dom'
import { DashboardUserTab } from './tabs/users/List'
import { DashboardMachinesTab } from './tabs/machines/List'

enum Tabs {
  Users = 'users',
  Machines = 'machines'
}

const loadMenuDefinition = (role: Roles, props: MenuProps): MenuSection[] => {
  switch (role) {
    case Roles.SuperAdmin:
      return superadminMenuSections(props)
    case Roles.Serviceman:
      return servicemanMenuSections()
    case Roles.CompanyManager:
      return companyManagerMenuSections(props)
    case Roles.User:
      return userMenuSections()
    case Roles.Operator:
      return operatorMenuSections()
    case Roles.Coordinator:
      return coordinatorMenuSections()
    default:
      console.error('Dashboard.loadMenuDefinition: Unsupported user role', role);
      return []
  }
}

export const Dashboard = () => {
  // const navigate = useNavigate()
  const { services, session, translates } = useAppContext()
  const responsive = useResponsiveChange()
  const { openModal } = useModal()
  const menuProps: MenuProps = {
    openModal
  }
  const menuSections: MenuSection[] | undefined = useMemo(() => session ? loadMenuDefinition(session.user.role as Roles, menuProps) : undefined, [session])

  if (!session || !menuSections || !translates) {
    return <div className='spinner'></div>
  }

  return <LoggedInLayout pageName='dashboard' services={services} session={session}
    navbarLeft={<LogoutButton services={services} translates={translates} />}
    navbarRight={<UserLangsButton session={session} services={services} translates={translates} />}  >
    {responsive.isDesktop ? <DesktopDashboard /> : <MobileDashboard />}
    {/* <MobileDashboard /> */}
  </LoggedInLayout>
};

export const MobileDashboard = () => {
  const { services, session, translates } = useAppContext()
  const responsive = useResponsiveChange()
  const { openModal } = useModal()
  const menuProps: MenuProps = {
    openModal
  }
  const menuSections: MenuSection[] | undefined = useMemo(() => session ? loadMenuDefinition(session.user.role as Roles, menuProps) : undefined, [session])

  if (!session || !menuSections || !translates) {
    return <div className='spinner'></div>
  }

  return <div className={`dashboard dashboard-mobile}`}>
    <Menu menuSections={menuSections} />
  </div>
};

export const DesktopDashboard = () => {
  // const navigate = useNavigate()
  const { services, session, translates } = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const menuProps: MenuProps = {
    openModal
  }
  const menuSections: MenuSection[] | undefined = useMemo(() => session ? loadMenuDefinition(session.user.role as Roles, menuProps) : undefined, [session])
  const [open, setOpen] = useState<Tabs | undefined>();

  if (!session || !menuSections || !translates) {
    return <div className='spinner'></div>
  }

  function toggleTab(tab: Tabs) {
    setOpen(o => {
      if (o === tab) {
        return undefined
      }
      return tab
    })
  }

  function renderTabContent() {
    switch (open) {
      case Tabs.Users:
        return <DashboardUserTab />
      case Tabs.Machines:
        return <DashboardMachinesTab />
      default:
        return 'Error: Undefined tab'
    }
  }

  return <div className={`dashboard dashboard-desktop`}>
    <div className='top-menu'>
      <TopMenu menuSections={menuSections} action={(section: MenuSection) => {
        if (section.items.length < 1) {
          return
        } else if (section.items.length < 2) {
          let item = section.items[0]
          if (item.action) {
            item.action(item)
          } else {
            if(!item.url) {
              console.error('item.url is required')
              return
            }
            navigate(item.url)
          }
        } else {
          openModal((props) => {
            return SectionMenuPopup({ ...props, section })
          })
        }
      }} />
    </div>
    <div className='dashboard-desktop-main-body'>
      <div className={`dashboard-desktop-left-menu ${open ? `dashboard-desktop-${open}-left-menu` : ''}`} style={{
        width: open !== undefined ? 300 : 0
      }}>
        <div className='dashboard-desktop-left-menu-content'>
          {open && renderTabContent()}
        </div>
      </div>
      <div className='dashboard-desktop-map'>
        <div className='dashboard-desktop-left-menu-icons'>
          {ManagerRoles.includes(session.user.role as Roles) ? <div className='dashboard-map-tab-users-btn' onClick={() => toggleTab(Tabs.Users)}>
            <img src={`/public/img/dashboard/tabs/users.svg`} />
          </div> : null}
          <div className='dashboard-map-tab-machines-btn' onClick={() => toggleTab(Tabs.Machines)}>
            <img src={`/public/img/dashboard/tabs/machines.svg`} />
          </div>
        </div>
        <Map />
      </div>
    </div>
  </div>
};

const Map = () => {
  const { services, session, translates } = useAppContext()

  if (!session) {
    return <Spinner />
  }

  if (session && session.user.role === Roles.SuperAdmin) {
    return <MapComponent fetchUrl='/api/communal/map' />
  }
  return <MapComponent fetchUrl='/api/communal/my/map' />
};
